<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="650"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar
            color="blue"
            size="46"
          >
            <v-icon
              color="white"
              size="30"
            >
              mdi-credit-card-wireless
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Restaurant.Tables.Dialog.SelectTerminal.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Restaurant.Tables.Dialog.SelectTerminal.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-0">
          <v-container fluid>
            <v-row
              v-if="hasActiveEftPosTerminals"
              dense
            >
              <template v-for="vendor in vendors">
                <template v-for="terminal in vendor.Terminals">
                  <template v-if="isTerminalAllowedForUser(terminal)">
                    <v-col
                      :key="`vendor-${vendor.Id}-terminal-${terminal.Id}-no-instalments`"
                      :cols="terminal.HasInstalments && !hideInstalments ? 6 : 12"
                      class="pr-0 "
                    >
                      <v-list class="pa-0">
                        <v-list-item
                          :class="isDarkMode ? 'grey darken-3' : 'grey lighten-3'"
                          class="rounded rounded-r-0 px-1 px-sm-3"
                          ripple
                          @click="onSelectTerminal(terminal, false)"
                        >
                          <v-list-item-avatar
                            class="mr-1 mr-sm-3"
                            size="32"
                          >
                            <v-icon
                              color="green"
                              size="32"
                            >
                              mdi-credit-card-wireless
                            </v-icon>
                          </v-list-item-avatar>

                          <v-list-item-content :class="isDarkMode ? 'white--text' : 'grey--text text--darken-2'">
                            <v-list-item-title class="text-left">
                              {{ terminal.Nickname }}
                            </v-list-item-title>

                            <v-list-item-subtitle class="text-caption">
                              {{ getVendorAcquirerTextById(vendor.Id, terminal.AcquirerCode) }}
                            </v-list-item-subtitle>

                            <v-list-item-subtitle class="text-caption">
                              {{ terminal.Manufactor }} - {{ terminal.Model }}
                            </v-list-item-subtitle>

                            <v-list-item-subtitle class="text-caption green--text text--lighten-2">
                              {{ $t('Restaurant.Tables.Dialog.SelectInstalments.Without') }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>

                    <v-col
                      v-if="terminal.HasInstalments && !hideInstalments"
                      :key="`vendor-${vendor.Id}-terminal-${terminal.Id}-with-instalments`"
                      :cols="terminal.HasInstalments && !hideInstalments ? 6 : 12"
                      class="pl-0"
                    >
                      <v-list class="pa-0">
                        <v-list-item
                          :class="isDarkMode ? 'grey darken-3' : 'grey lighten-3'"
                          class="rounded rounded-l-0 px-1 px-sm-3"
                          ripple
                          @click="onSelectTerminal(terminal, true)"
                        >
                          <v-list-item-avatar
                            class="mr-1 mr-sm-3"
                            size="32"
                          >
                            <v-icon
                              color="orange"
                              size="32"
                            >
                              mdi-credit-card-wireless
                            </v-icon>
                          </v-list-item-avatar>

                          <v-list-item-content :class="isDarkMode ? 'white--text' : 'grey--text text--darken-2'">
                            <v-list-item-title class="text-left">
                              {{ terminal.Nickname }}
                            </v-list-item-title>

                            <v-list-item-subtitle class="text-caption">
                              {{ getVendorAcquirerTextById(vendor.Id, terminal.AcquirerCode) }}
                            </v-list-item-subtitle>

                            <v-list-item-subtitle class="text-caption">
                              {{ terminal.Manufactor }} - {{ terminal.Model }}
                            </v-list-item-subtitle>

                            <v-list-item-subtitle class="text-caption orange--text text--lighten-2">
                              {{ $t('Restaurant.Tables.Dialog.SelectInstalments.With') }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </template>
                </template>
              </template>
            </v-row>

            <v-row v-else>
              <v-col
                class="text-center justify-center"
                cols="12"
              >
                <v-data-table-no-data
                  :no-data-obj="$t('Restaurant.Tables.Dialog.SelectTerminal.NoData')"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            text
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <select-payment-instalments-dialog
      v-if="!hideInstalments"
      :terminal="selectedTerminal"
      :visible.sync="instalmentsDialogVisible"
      @instalments:selected="selectTerminal"
    />
  </div>
</template>

<script>
import AppData from '@/mixins/appdata'
import SelectPaymentInstalmentsDialog from '@/components/payments/orders/SelectPaymentInstalmentsDialog.vue'
import TerminalsCommon from '@/mixins/payments/terminalsCommon'
import VDataTableNoData from '@/components/common/data-table/VDataTableNoData.vue'

export default {
  name      : 'SelectPaymentTerminalDialog',
  components: {VDataTableNoData, SelectPaymentInstalmentsDialog},
  mixins    : [AppData, TerminalsCommon],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },

    hideInstalments: {
      type   : Boolean,
      default: false
    },

    filterVendor: {
      type   : String,
      default: ''
    }
  },
  data() {
    return {
      selectedTerminal        : null,
      instalmentsDialogVisible: false
    }
  },
  computed: {
    hasActiveEftPosTerminals() {
      let retVal = false


      this.vendors.forEach(vendor => {
        const vendorHasActiveTerminal = vendor?.Terminals?.some(terminal => this.isTerminalAllowedForUser(terminal))
        if (vendorHasActiveTerminal) retVal = true
      })

      return retVal
    },

    vendors() {
      const vendors = this.appConfig?.POS_VENDORS || []
      return vendors.filter(vendor => this.filterVendor ? vendor.Code === this.filterVendor : true)
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    isVisible(val) {
      if (val) this.selectedTerminal = null
    }
  },
  methods: {
    isTerminalAllowedForUser(terminal) {
      const active = this.posUser?.Settings?.AllUsers?.ActiveEftPos?.Active ?? false
      const items = this.posUser?.Settings?.AllUsers?.ActiveEftPos?.Items || []

      if (!active) return !!terminal?.Active

      return !!terminal?.Active && items?.includes(terminal?.Id)
    },

    onSelectTerminal(terminal, withInstalments = false) {
      this.selectedTerminal = terminal
      if (withInstalments) {
        this.instalmentsDialogVisible = true
      } else {
        this.selectTerminal(terminal, 0)
      }
    },

    selectTerminal(terminal, instalments = 0) {
      this.isVisible = false
      this.$emit('terminal:selected', terminal, instalments)
    },

    onCancelButtonClick() {
      this.isVisible = false
    }
  }
}
</script>
